import cx from 'classnames';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Message } from '@wg/wows-react-uikit';

import settings from '~/settings';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { scrollToY } from '~/helpers/animate';
import { showClanTasksRewardsDialog } from '~/helpers/dialogs';
import { t } from '~/helpers/localization';
import { usePoshtaMarkersDeactivate } from '~/hooks/usePoshta';
import { iHavePermission, PERMISSIONS } from '~/roles';
import { useAppDispatch, useAppSelector } from '~/store';
import {
  clanTasksClaimedSelector,
  clanTasksIsClaimingSelector,
  clanTasksIsFetchingSelector,
  clanTasksTasksSelector,
} from '~/store/selectors/clanTasksSelectors';
import { currentAccountSelector } from '~/store/selectors/currentAccountSelector';
import { POSHTA_MARKER_NAME, POSHTA_MARKER_SOURCE } from '~/types/poshta';
import { claimClanTaskThunk, getClanTasksThunk, type ClanTask, type ClanTaskReward } from '~/Actions/ActionClanTasks';

import TaskRow from '~/pages/ClanTasks/TaskRow/TaskRow';
import TaskRowAchievement, { type TaskRowAchievementHandle } from '~/pages/ClanTasks/TaskRow/TaskRowAchievement';
import { AnimatedFetchingComponent, StepCSSTransitionGroup } from '~/UIKit';

import styles from './ClanTasks.scss';

const CONTENT_PADDING = 0;
const ROW_HEIGHT = 98;

const byOrder = (a: ClanTask, b: ClanTask) => a.order - b.order;

const pagePoshtaMarkers = [{ source: POSHTA_MARKER_SOURCE.CLAN_MISSIONS, name: POSHTA_MARKER_NAME.REWARD_AVAILABLE }];

const ClanTasks = () => {
  const dispatch = useAppDispatch();

  const currentAccount = useAppSelector(currentAccountSelector);
  const isClaiming = useAppSelector(clanTasksIsClaimingSelector);
  const isFetching = useAppSelector(clanTasksIsFetchingSelector);
  const claimedProp = useAppSelector(clanTasksClaimedSelector);
  const tasks = useAppSelector(clanTasksTasksSelector);

  const [state, setState] = useState({
    firstOpen: true,
    showShadowTop: false,
    showShadowBottom: false,
  });

  const [isReady, setIsReady] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const taskRowRefs = useRef<Map<number, React.ElementRef<typeof TaskRow>>>(new Map());
  const achievementRef = useRef<TaskRowAchievementHandle | null>(null);

  const canClaimRewards = useMemo(() => iHavePermission(PERMISSIONS.CHANGE_SETTINGS, currentAccount), [currentAccount]);

  usePoshtaMarkersDeactivate(pagePoshtaMarkers);

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrollPosition = e.currentTarget.scrollTop;
    const scrollHeight = e.currentTarget.scrollHeight - e.currentTarget.clientHeight - CONTENT_PADDING;

    setState((s) => ({
      ...s,
      showShadowTop: scrollPosition >= CONTENT_PADDING,
      showShadowBottom: scrollPosition <= scrollHeight,
    }));
  }, []);

  const getAggregatedReward = useCallback((): ClanTaskReward | null => {
    const aggregatedReward = { oil: 0, coal: 0 };

    tasks.forEach((task) => {
      if (task.isFinished && !task.isClaimed && task.reward) {
        aggregatedReward.oil += task.reward.oil || 0;
        aggregatedReward.coal += task.reward.coal || 0;
      }
    });

    return aggregatedReward.oil > 0 || aggregatedReward.coal > 0 ? aggregatedReward : null;
  }, [tasks]);

  useEffect(() => {
    dwhExport.push(DWH_EVENTS.CLAN_TASKS);

    void dispatch(getClanTasksThunk()).then(() => {
      setIsReady(true);
    });

    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }

    const containerHasScroll = el.scrollHeight > el.clientHeight;
    setState((s) => ({ ...s, showShadowBottom: containerHasScroll }));
  }, [dispatch]);

  useEffect(() => {
    if (!isReady || isFetching || !canClaimRewards) {
      return;
    }

    if (!state.firstOpen) {
      return;
    }

    const aggregatedReward = getAggregatedReward();
    setState((s) => ({ ...s, firstOpen: false }));

    if (aggregatedReward !== null) {
      dispatch(showClanTasksRewardsDialog(tasks, aggregatedReward));
    }
  }, [canClaimRewards, dispatch, getAggregatedReward, isFetching, state.firstOpen, tasks, isReady]);

  const animateTaskRow = useCallback((taskId: number, index: number, speed = 100) => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    scrollToY(container, index * ROW_HEIGHT + CONTENT_PADDING, speed);

    window.setTimeout(() => {
      const row = taskRowRefs.current.get(taskId);
      row?.animate?.();
    }, speed);
  }, []);

  const prevClaimedRef = useRef<ClanTask[]>(claimedProp);

  useEffect(() => {
    const prevClaimed = prevClaimedRef.current;
    prevClaimedRef.current = claimedProp;

    const newClaimed = claimedProp.filter((task) => !prevClaimed.includes(task));
    const availableLength = tasks.filter((t) => !t.isFinished).length;

    let delay = 0;
    newClaimed.forEach((claimedTask) => {
      const index = newClaimed.findIndex((task) => task.name === claimedTask.name);
      if (index > -1) {
        window.setTimeout(() => {
          animateTaskRow(claimedTask.taskId, availableLength + index, 500);
        }, delay);
        delay += 1300;
      }
    });
  }, [animateTaskRow, claimedProp, tasks]);

  const available = useMemo(() => tasks.filter((t) => !t.isFinished).sort(byOrder), [tasks]);
  const completed = useMemo(() => tasks.filter((t) => t.isFinished && !t.isClaimed).sort(byOrder), [tasks]);
  const claimed = useMemo(() => tasks.filter((t) => t.isFinished && t.isClaimed).sort(byOrder), [tasks]);
  const sortedCompletedTasks = useMemo(() => [...completed, ...claimed], [claimed, completed]);

  const syncMessage = useMemo(() => {
    return t(
      'Информация о прогрессе выполнения задач обновляется  ежедневно в {update_time} по серверному времени.',
    ).replace('{update_time}', settings.darwin.syncTime);
  }, []);

  const claimClanTask = (taskId: number) => {
    void dispatch(claimClanTaskThunk(taskId));
  };

  return (
    <div className={styles.wrapper}>
      <AnimatedFetchingComponent className={styles.subWrapper} isFetching={isFetching}>
        <StepCSSTransitionGroup className={styles.header} level={1}>
          <span className={styles.pageTitle}>{t('Задачи клана')}</span>
        </StepCSSTransitionGroup>

        <div
          className={cx(styles.taskContainer, {
            [styles.showShadowTop]: state.showShadowTop,
            [styles.showShadowBottom]: state.showShadowBottom,
          })}
        >
          <div className={styles.taskContainerContent} ref={scrollContainerRef} onScroll={onScroll}>
            {available.map((task, index) => (
              <StepCSSTransitionGroup level={index + 2} key={task.taskId} className={styles.taskContainerItem}>
                <TaskRow
                  task={task}
                  canClaimRewards={canClaimRewards}
                  onClaim={() => claimClanTask(task.taskId)}
                  isClaiming={isClaiming}
                  ref={(ref) => {
                    if (ref) {
                      taskRowRefs.current.set(task.taskId, ref);
                    } else {
                      taskRowRefs.current.delete(task.taskId);
                    }
                  }}
                />
              </StepCSSTransitionGroup>
            ))}

            <StepCSSTransitionGroup
              level={available.length + 2}
              className={cx(styles.taskContainerItem, {
                [styles.orderDown]: !available.length && !completed.length,
              })}
            >
              <TaskRowAchievement ref={achievementRef} tasks={tasks} />
            </StepCSSTransitionGroup>

            {sortedCompletedTasks.map((task, index) => (
              <StepCSSTransitionGroup
                level={available.length + index + 2}
                key={task.taskId}
                className={styles.taskContainerItem}
              >
                <TaskRow
                  task={task}
                  canClaimRewards={canClaimRewards}
                  onClaim={() => claimClanTask(task.taskId)}
                  isClaiming={isClaiming}
                  ref={(ref) => {
                    if (ref) {
                      taskRowRefs.current.set(task.taskId, ref);
                    } else {
                      taskRowRefs.current.delete(task.taskId);
                    }
                  }}
                />
              </StepCSSTransitionGroup>
            ))}
          </div>
        </div>

        <StepCSSTransitionGroup className={styles.syncMessageWrapper} level={3}>
          <Message style="inline" type="confirmation">
            &nbsp;&nbsp;{syncMessage}
          </Message>
        </StepCSSTransitionGroup>
      </AnimatedFetchingComponent>
    </div>
  );
};

export default memo(ClanTasks);
